import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface ScenarioResults {
  summary: {
    tenYearNetBenefits: number;
    tenYearPresentValue: number;
    totalIncentive: number;
    paybackPeriodYears: number | null;
    rateOfReturn: number | null;
    perJobIncentiveCost: number;
  };
}

interface Scenario {
  id: string;
  analysisId: string;
  name: string;
  description?: string | null;
  status: 'draft' | 'ready';
  inputOverrides: Record<string, unknown>;
  results?: ScenarioResults | null;
}

interface ApiIssue {
  path?: Array<string | number>;
  message?: string;
}

async function readApiError(res: Response): Promise<string> {
  let payload: { error?: string; issues?: ApiIssue[] } | null = null;
  try {
    payload = (await res.json()) as { error?: string; issues?: ApiIssue[] };
  } catch {
    payload = null;
  }

  const issues = payload?.issues ?? [];
  if (issues.length > 0) {
    return issues
      .map((issue) => {
        const label = issue.path && issue.path.length > 0 ? issue.path.join('.') : 'scenario';
        return `${label}: ${issue.message ?? 'Invalid value'}`;
      })
      .join('\n');
  }

  return payload?.error ?? `${res.status}`;
}

function fmtCurrency(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

type OverrideKey =
  | 'jobGrantPctOfPayroll'
  | 'jobGrantYears'
  | 'infrastructureGrantTotal'
  | 'infrastructureGrantYears'
  | 'propertyTaxAbatementPct'
  | 'propertyTaxAbatementYears'
  | 'discountRate'
  | 'countyPropertyTaxRate'
  | 'isdPropertyTaxRate'
  | 'propertyTaxRate'
  | 'directJobs'
  | 'directAnnualPayroll';

interface FieldDef {
  key: OverrideKey;
  label: string;
  hint?: string;
  min?: number;
  max?: number;
  step?: number;
}

const INCENTIVE_FIELDS: FieldDef[] = [
  {
    key: 'jobGrantPctOfPayroll',
    label: 'Job grant (% of payroll)',
    hint: 'e.g. 0.05 for 5%',
    min: 0,
    max: 1,
    step: 0.01,
  },
  { key: 'jobGrantYears', label: 'Job grant years', min: 0, max: 20, step: 1 },
  { key: 'infrastructureGrantTotal', label: 'Infrastructure grant total ($)', min: 0, step: 10000 },
  {
    key: 'infrastructureGrantYears',
    label: 'Infrastructure grant years',
    min: 1,
    max: 20,
    step: 1,
  },
  {
    key: 'propertyTaxAbatementPct',
    label: 'Property tax abatement (%)',
    hint: 'e.g. 0.50 for 50%',
    min: 0,
    max: 1,
    step: 0.05,
  },
  { key: 'propertyTaxAbatementYears', label: 'Abatement years', min: 0, max: 20, step: 1 },
  {
    key: 'discountRate',
    label: 'Discount rate',
    hint: 'e.g. 0.05 for 5%',
    min: 0,
    max: 0.3,
    step: 0.005,
  },
];

const RATES_FIELDS: FieldDef[] = [
  {
    key: 'propertyTaxRate',
    label: 'City property tax rate',
    hint: 'e.g. 0.005',
    min: 0,
    max: 0.05,
    step: 0.0001,
  },
  {
    key: 'countyPropertyTaxRate',
    label: 'County property tax rate',
    hint: 'e.g. 0.004',
    min: 0,
    max: 0.05,
    step: 0.0001,
  },
  {
    key: 'isdPropertyTaxRate',
    label: 'ISD property tax rate',
    hint: 'e.g. 0.013',
    min: 0,
    max: 0.05,
    step: 0.0001,
  },
];

const JOBS_FIELDS: FieldDef[] = [
  { key: 'directJobs', label: 'Direct jobs', min: 1, step: 1 },
  { key: 'directAnnualPayroll', label: 'Annual direct payroll ($)', min: 1, step: 10000 },
];

export function ScenarioEditorRoute() {
  const { id: analysisId, scenarioId } = useParams<{ id: string; scenarioId: string }>();
  const getToken = useSessionTokenGetter();
  const navigate = useNavigate();
  const isNew = scenarioId === 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [overrides, setOverrides] = useState<Partial<Record<OverrideKey, string>>>({});
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew || !scenarioId || !analysisId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const json = await res.json();
        const found = (json.scenarios as Scenario[]).find((s) => s.id === scenarioId);
        if (!found) {
          setError('Scenario not found');
          return;
        }
        if (!cancelled) {
          setScenario(found);
          setName(found.name);
          setDescription(found.description ?? '');
          const ov: Partial<Record<OverrideKey, string>> = {};
          for (const [k, v] of Object.entries(found.inputOverrides)) {
            ov[k as OverrideKey] = String(v);
          }
          setOverrides(ov);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scenarioId, analysisId, isNew, getToken]);

  function setOverride(key: OverrideKey, value: string) {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }

  function buildInputOverrides() {
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(overrides)) {
      if (v !== '' && v !== undefined) {
        const num = Number(v);
        if (!Number.isNaN(num)) result[k] = num;
      }
    }
    return result;
  }

  async function handleSave() {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        inputOverrides: buildInputOverrides(),
      };
      const url = isNew
        ? `${API}/api/v1/analyses/${analysisId}/scenarios`
        : `${API}/api/v1/analyses/${analysisId}/scenarios/${scenarioId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await readApiError(res));
      }
      const json = await res.json();
      const saved = json.scenario as Scenario;
      setScenario(saved);
      if (isNew) navigate(`/analyses/${analysisId}/scenarios/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndRun() {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      let currentId = scenarioId;

      // Save first
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        inputOverrides: buildInputOverrides(),
      };
      const saveUrl = isNew
        ? `${API}/api/v1/analyses/${analysisId}/scenarios`
        : `${API}/api/v1/analyses/${analysisId}/scenarios/${scenarioId}`;
      const saveMethod = isNew ? 'POST' : 'PUT';
      const saveRes = await fetch(saveUrl, {
        method: saveMethod,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!saveRes.ok) {
        throw new Error(await readApiError(saveRes));
      }
      const saveJson = await saveRes.json();
      const saved = saveJson.scenario as Scenario;
      currentId = saved.id;
      setSaving(false);

      // Run
      setRunning(true);
      const runRes = await fetch(
        `${API}/api/v1/analyses/${analysisId}/scenarios/${currentId}/run`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!runRes.ok) {
        throw new Error(await readApiError(runRes));
      }
      const runJson = await runRes.json();
      const ran = runJson.scenario as Scenario;
      setScenario(ran);
      if (isNew) navigate(`/analyses/${analysisId}/scenarios/${currentId}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
      setRunning(false);
    }
  }

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  const res = scenario?.results ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/analyses/${analysisId}/scenarios`}
          className="text-xs text-slate-400 hover:underline"
        >
          ← Scenarios
        </Link>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight">
          {isNew ? 'New scenario' : name || 'Edit scenario'}
        </h2>
      </div>

      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-6">
        {/* Left: form */}
        <div className="col-span-2 space-y-6">
          {/* Basic info */}
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-semibold text-slate-800">Scenario details</h3>
            <div>
              <label htmlFor="scenario-name" className="block text-xs font-medium text-slate-600">
                Name *
              </label>
              <input
                id="scenario-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                placeholder="e.g. 5-year abatement, 50%"
              />
            </div>
            <div>
              <label
                htmlFor="scenario-description"
                className="block text-xs font-medium text-slate-600"
              >
                Description
              </label>
              <textarea
                id="scenario-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                placeholder="Optional notes about this scenario"
              />
            </div>
          </div>

          {/* Override groups */}
          {[
            { title: 'Incentive package', fields: INCENTIVE_FIELDS },
            { title: 'Tax rates', fields: RATES_FIELDS },
            { title: 'Direct employment', fields: JOBS_FIELDS },
          ].map(({ title, fields }) => (
            <div key={title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-slate-800">{title}</h3>
              <p className="mb-4 text-xs text-slate-400">
                Leave blank to use the base analysis value.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.key}>
                    <label
                      htmlFor={`override-${f.key}`}
                      className="block text-xs font-medium text-slate-600"
                    >
                      {f.label}
                    </label>
                    {f.hint && <p className="text-xs text-slate-400">{f.hint}</p>}
                    <input
                      id={`override-${f.key}`}
                      type="number"
                      value={overrides[f.key] ?? ''}
                      onChange={(e) => setOverride(f.key, e.target.value)}
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                      placeholder="(base)"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || running}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => void handleSaveAndRun()}
              disabled={saving || running}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {running ? 'Running…' : saving ? 'Saving…' : 'Save & Run'}
            </button>
          </div>
        </div>

        {/* Right: results panel */}
        <div className="space-y-4">
          {res ? (
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-semibold text-slate-800">Last run results</h3>
              {(
                [
                  {
                    label: '10-yr net benefit',
                    value: fmtCurrency(res.summary.tenYearNetBenefits),
                  },
                  {
                    label: '10-yr present value',
                    value: fmtCurrency(res.summary.tenYearPresentValue),
                  },
                  { label: 'Total incentive', value: fmtCurrency(res.summary.totalIncentive) },
                  {
                    label: 'Payback period',
                    value:
                      res.summary.paybackPeriodYears != null
                        ? `${res.summary.paybackPeriodYears.toFixed(1)} yrs`
                        : 'N/A',
                  },
                  {
                    label: 'Rate of return',
                    value:
                      res.summary.rateOfReturn != null
                        ? `${(res.summary.rateOfReturn * 100).toFixed(1)}%`
                        : 'N/A',
                  },
                  {
                    label: 'Cost per job',
                    value:
                      res.summary.perJobIncentiveCost > 0
                        ? fmtCurrency(res.summary.perJobIncentiveCost)
                        : '—',
                  },
                ] as { label: string; value: string }[]
              ).map(({ label, value }) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-medium text-slate-900">{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-5 text-center">
              <p className="text-sm text-slate-500">Save & Run to see results</p>
            </div>
          )}

          <Link
            to={`/analyses/${analysisId}/scenarios`}
            className="block text-center text-xs text-slate-400 hover:underline"
          >
            Back to all scenarios
          </Link>
        </div>
      </div>
    </div>
  );
}
